import { tracingAxios } from '../Sentry.js'
import { Environment } from '../../environment.js'
import type { SpinRun } from '../../Generated/SpinRun.ts'
import type { SpinMetricsResponse } from '../../Generated/SpinMetricsResponse.ts'
import type { SpinCohesionResponse } from '../../Generated/SpinCohesionResponse.ts'
import type { RunTransition } from '../../Generated/RunTransition.ts'
import type { MembershipFlowResponse } from '../../Generated/MembershipFlowResponse.ts'
import type { SpinDetail } from '../../Generated/SpinDetail.ts'
import type { AlgorithmCompareResponse } from '../../Generated/AlgorithmCompareResponse.ts'
import type { DriftResponse } from '../../Generated/DriftResponse.ts'
import type { SpinReferenceResponse } from '../../Generated/SpinReferenceResponse.ts'
import type { RefDrilldown } from '../../Generated/RefDrilldown.ts'
import type { HighIdentitySingletonResponse } from '../../Generated/HighIdentitySingletonResponse.ts'
import type { SpinLeaderboardResponse } from '../../Generated/SpinLeaderboardResponse.ts'

// Response/DTO shapes are generated from the backend @TSExported data classes in io.spcfy.spin.*
// (regenerated on dev-server startup / the TS generator). Do not hand-edit them here — change the
// Kotlin DTO and regenerate. This module only holds the HTTP calls.

/** Reference identifier a SPIN set can be compared against — matches the backend SpinReferenceKind enum. */
export type SpinReferenceKind = 'NCBI_ID' | 'NCBI_TAXID' | 'BOLD_BIN'

/** Ordering for the reference `mostSplit` list — matches the backend RefSplitOrder enum. */
export type RefSplitOrder = 'SPINS' | 'OTUS' | 'SUSPICION'

export const fetchSpinDrift = async (
  runId: string,
  filters: { rank?: number; source?: string } = {},
  limit = 100
): Promise<DriftResponse> => {
  const { data } = await tracingAxios.get<DriftResponse>(`${Environment.API_URL}/admin/spin/drift`, {
    // rank omitted ⇒ all ranks (backend default -1); source omitted ⇒ all sources.
    params: {
      runId,
      limit,
      ...(filters.rank != null ? { rank: filters.rank } : {}),
      ...(filters.source ? { source: filters.source } : {})
    },
    withCredentials: true
  })
  return data
}

/**
 * Reference concordance: how the current SPIN set splits/keeps a per-OTU reference id (NCBI id / NCBI
 * taxon id / BOLD bin). `orderBy` and `minHitPercentage` are OPTIONAL and omitted from the request when
 * absent, so the backend defaults apply (orderBy=SUSPICION, no identity floor) exactly as before.
 * `minHitPercentage` (e.g. 99.5) keeps only ids whose WORST member identity is at least that — the
 * "split despite a near-perfect match" cohort.
 */
export const fetchSpinReference = async (
  algorithmType: string,
  asOf: string,
  refKind: SpinReferenceKind,
  amplicon?: string | null,
  orderBy?: RefSplitOrder,
  minHitPercentage?: number
): Promise<SpinReferenceResponse> => {
  const { data } = await tracingAxios.get<SpinReferenceResponse>(`${Environment.API_URL}/admin/spin/reference`, {
    params: {
      algorithmType,
      asOf,
      refKind,
      ...(amplicon ? { amplicon } : {}),
      ...(orderBy ? { orderBy } : {}),
      ...(minHitPercentage != null ? { minHitPercentage } : {})
    },
    withCredentials: true
  })
  return data
}

/**
 * Drill-down for ONE reference id: which current SPINs it is split across, the member OTUs carrying it
 * in each, and the candidate explanations (amplicon, singleton-vs-cluster, purity, taxonomy, core length,
 * lineage, cross-source id). `limit` caps the SPIN list ONLY — the response's `spinCount` is the true
 * total and `truncated` flags the cap, so the caller must disclose it.
 *
 * PERFORMANCE: fast (well under a second) for NCBI_ID / BOLD_BIN, but the `NCBI_TAXID` seed column is
 * unindexed — measured ~4.4s warm / ~40s cold per statement and this endpoint runs three, so a cold
 * taxid drill-down is minutes. Callers must make NCBI_TAXID an explicit, opt-in load (never on mount).
 */
export const fetchSpinReferenceDetail = async (
  algorithmType: string,
  asOf: string,
  refKind: SpinReferenceKind,
  refId: string,
  amplicon?: string | null,
  limit?: number
): Promise<RefDrilldown> => {
  const { data } = await tracingAxios.get<RefDrilldown>(`${Environment.API_URL}/admin/spin/reference/detail`, {
    params: {
      algorithmType,
      asOf,
      ...(amplicon ? { amplicon } : {}),
      refKind,
      refId,
      ...(limit != null ? { limit } : {})
    },
    withCredentials: true
  })
  return data
}

export const fetchSpinCompare = async (
  a: string,
  b: string,
  amplicon?: string | null
): Promise<AlgorithmCompareResponse> => {
  const { data } = await tracingAxios.get<AlgorithmCompareResponse>(`${Environment.API_URL}/admin/spin/compare`, {
    // amplicon omitted ⇒ compare across all amplicons; a concrete value scopes both sides to it.
    params: { a, b, ...(amplicon ? { amplicon } : {}) },
    withCredentials: true
  })
  return data
}

export const fetchSpinRuns = async (): Promise<SpinRun[]> => {
  const { data } = await tracingAxios.get<SpinRun[]>(`${Environment.API_URL}/admin/spin/runs`, {
    withCredentials: true
  })
  return data
}

export const fetchSpinMetrics = async (
  algorithmType: string,
  asOf: string,
  amplicon?: string | null,
  limit = 1000
): Promise<SpinMetricsResponse> => {
  const { data } = await tracingAxios.get<SpinMetricsResponse>(`${Environment.API_URL}/admin/spin/metrics`, {
    // amplicon omitted (undefined) ⇒ backend spans all amplicons; a concrete value scopes the set.
    params: { algorithmType, asOf, ...(amplicon ? { amplicon } : {}), limit },
    withCredentials: true
  })
  return data
}

/**
 * Full-set cohesion (unity/reach over EVERY current SPIN). Heavy on the backend, so call it only on
 * explicit user action — never on mount. No `limit`: the response is compact per-source aggregates.
 */
export const fetchSpinCohesion = async (
  algorithmType: string,
  asOf: string,
  amplicon?: string | null
): Promise<SpinCohesionResponse> => {
  const { data } = await tracingAxios.get<SpinCohesionResponse>(`${Environment.API_URL}/admin/spin/cohesion`, {
    params: { algorithmType, asOf, ...(amplicon ? { amplicon } : {}) },
    withCredentials: true
  })
  return data
}

/**
 * The current SPINs that hold a SINGLE member OTU which nonetheless matched an NCBI reference at
 * >= `minHitPercentage` — "near-perfect match to a known sequence, yet clustering alone".
 *
 * HEAVY (~3.3s measured warm): singleton-ness is a property of the whole resolved current set, so there
 * is no reference id to seed from and no shortcut. Call it only on explicit user action — never on mount.
 * `limit` caps the returned list ONLY; `totalCount` is the true cohort size and `truncated` flags the cap,
 * so the caller MUST disclose it. Ordering is summed reads DESC (identity saturates at 100%, so reads is
 * what makes a capped page meaningful).
 */
export const fetchSpinHighIdentitySingletons = async (
  algorithmType: string,
  asOf: string,
  amplicon?: string | null,
  minHitPercentage?: number,
  limit?: number
): Promise<HighIdentitySingletonResponse> => {
  const { data } = await tracingAxios.get<HighIdentitySingletonResponse>(
    `${Environment.API_URL}/admin/spin/high-identity-singletons`,
    {
      params: {
        algorithmType,
        asOf,
        ...(amplicon ? { amplicon } : {}),
        ...(minHitPercentage != null ? { minHitPercentage } : {}),
        ...(limit != null ? { limit } : {})
      },
      withCredentials: true
    }
  )
  return data
}

/**
 * "Top SPINs": the biggest current SPINs by member OTUs, distinct member sequences, distinct orders and
 * summed reads. Only each order's LATEST report contributes members, so a re-crunched order can't count twice.
 *
 * HEAVY (~7.4s measured warm): a whole-set aggregation over every current member, with no seed to start
 * from. Call it only on explicit user action — never on mount.
 *
 * Returns FOUR ranked lists rather than taking a sort parameter. That is deliberate and the view depends
 * on it: only a top-N is shippable out of 435k SPINs, so re-sorting one list by another metric on the
 * client would silently omit the SPINs that belong in that ranking. Switch lists, don't re-sort.
 * `limit` caps each list independently and does not bound the scan, so raising it is nearly free.
 */
export const fetchSpinLeaderboard = async (
  algorithmType: string,
  asOf: string,
  amplicon?: string | null,
  limit?: number
): Promise<SpinLeaderboardResponse> => {
  const { data } = await tracingAxios.get<SpinLeaderboardResponse>(`${Environment.API_URL}/admin/spin/leaderboard`, {
    params: {
      algorithmType,
      asOf,
      ...(amplicon ? { amplicon } : {}),
      ...(limit != null ? { limit } : {})
    },
    withCredentials: true
  })
  return data
}

export const fetchSpinLineage = async (algorithmType: string): Promise<RunTransition[]> => {
  const { data } = await tracingAxios.get<RunTransition[]>(`${Environment.API_URL}/admin/spin/lineage`, {
    params: { algorithmType },
    withCredentials: true
  })
  return data
}

// limit 0 ⇒ the backend returns the FULL per-SPIN list (every SPIN the run minted); the table sorts client-side.
export const fetchSpinMembershipFlow = async (runId: string, limit = 0): Promise<MembershipFlowResponse> => {
  const { data } = await tracingAxios.get<MembershipFlowResponse>(`${Environment.API_URL}/admin/spin/membership-flow`, {
    params: { runId, limit },
    withCredentials: true
  })
  return data
}

export const fetchSpinDetail = async (spinId: string): Promise<SpinDetail> => {
  const { data } = await tracingAxios.get<SpinDetail>(`${Environment.API_URL}/admin/spin/detail/${spinId}`, {
    withCredentials: true
  })
  return data
}
